import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { format, addDays, startOfDay } from "date-fns";
import { api } from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Calendar } from "@/components/ui/calendar";
import Magnetic from "@/components/Magnetic";

const NEEDS = ["Branding", "Social Media", "Packaging", "AI Creatives", "AI Videos", "UI UX", "Catalog/Publication", "Other"];
const BUDGETS = ["Under ₹50k", "₹50k – 1.5L", "₹1.5L – 5L", "₹5L+"];
const TIMELINES = ["ASAP", "Within a month", "1–3 months", "Just exploring"];
const SLOTS = ["12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM", "6:00 PM", "7:30 PM", "9:00 PM"];

const inputCls = "w-full bg-transparent border-b border-border focus:border-accent outline-none py-3 text-lg text-ink transition-colors duration-300 placeholder:text-muted2/60";

export default function Connect() {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", brand: "", handle: "", needs: [], budget: "", timeline: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [bookedTimes, setBookedTimes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleNeed = (n) =>
    setForm((f) => ({ ...f, needs: f.needs.includes(n) ? f.needs.filter((x) => x !== n) : [...f.needs, n] }));

  // When a date is chosen, load the slots already taken for that day
  useEffect(() => {
    if (!date) {
      setBookedTimes([]);
      return;
    }
    const key = format(date, "EEE, d MMM yyyy");
    let active = true;
    api
      .get("/booked-slots", { params: { date: key } })
      .then((res) => {
        if (!active) return;
        const taken = res.data?.times || [];
        setBookedTimes(taken);
        // if the currently picked time just became unavailable, clear it
        setTime((t) => (taken.includes(t) ? "" : t));
      })
      .catch(() => active && setBookedTimes([]));
    return () => {
      active = false;
    };
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("We'll need your name at least.");
    if (!form.email.trim() && !form.whatsapp.trim()) return toast.error("Add an email or WhatsApp so we can reach you.");
    if (!date || !time) return toast.error("Pick a date and time for your call.");
    if (bookedTimes.includes(time)) return toast.error("That slot is already booked. Please pick another time.");
    if (form.needs.length === 0) return toast.error("Pick at least one thing you need.");
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, preferred_date: format(date, "EEE, d MMM yyyy"), preferred_time: time });
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 409) {
        // slot got taken in the meantime — refresh availability and nudge the user
        toast.error("That slot was just booked. Please pick another time.");
        setBookedTimes((b) => (b.includes(time) ? b : [...b, time]));
        setTime("");
      } else {
        toast.error("Something broke on our end. Try again?");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 md:pt-44 w-full max-w-[1600px] mx-auto px-5 md:px-10 pb-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-xs uppercase tracking-[0.3em] text-muted2 mb-6">Book a call</p>
          <h1 className="font-serif text-6xl md:text-7xl tracking-tighter leading-[0.85]">
            <MaskedLines lines={["Start", <>your <span className="italic text-accent">qissa.</span></>]} />
          </h1>
          <Reveal delay={0.3}>
            <p className="text-muted2 text-lg mt-8 leading-relaxed max-w-sm">
              Tell us about your brand, pick a date and time that suits you, and leave your details. We'll confirm the online meeting over email or WhatsApp.
            </p>
            <div className="mt-10 space-y-3 text-sm text-muted2">
              <p className="flex items-center gap-3"><span className="text-accent">✦</span> We reply to every serious enquiry.</p>
              <p className="flex items-center gap-3"><span className="text-accent">✦</span> No packages, no pricing games.</p>
              <p className="flex items-center gap-3"><span className="text-accent">✦</span> We'll tell you honestly if we're not the fit.</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.form key="form" onSubmit={submit} exit={{ opacity: 0, y: -20 }} className="space-y-10" data-testid="intake-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2">Your name *</label>
                    <input data-testid="input-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2">Brand name</label>
                    <input data-testid="input-brand" className={inputCls} value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="Your brand" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2">Email</label>
                    <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@brand.com" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2">WhatsApp</label>
                    <input data-testid="input-whatsapp" className={inputCls} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91 …" />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted2">Website or Instagram handle</label>
                  <input data-testid="input-handle" className={inputCls} value={form.handle} onChange={(e) => set("handle", e.target.value)} placeholder="@yourbrand or yourbrand.com" />
                </div>

                {/* Date + time picker */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2 block mb-4">Pick a date *</label>
                    <div className="rounded-xl border border-border bg-surface p-2 inline-block" data-testid="booking-calendar">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={[{ before: addDays(startOfDay(new Date()), 1) }, { dayOfWeek: [0] }]} />
                    </div>
                    <p className="text-xs text-muted2 mt-3">Bookings open from tomorrow onwards. We're closed on Sundays.</p>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2 block mb-4">Pick a time *</label>
                    <div className="grid grid-cols-2 gap-2" data-testid="time-group">
                      {SLOTS.map((s) => {
                        const isBooked = bookedTimes.includes(s);
                        return (
                          <button
                            key={s} type="button"
                            onClick={() => !isBooked && setTime(s)}
                            disabled={isBooked}
                            aria-disabled={isBooked}
                            data-testid={`time-${s.replace(/[^a-zA-Z0-9]/g, "")}`}
                            className={`relative rounded-lg px-4 py-3 text-sm border transition-colors duration-300 ${
                              isBooked
                                ? "border-border/50 text-muted2/40 line-through cursor-not-allowed bg-surface/40"
                                : time === s
                                ? "border-accent text-ink bg-accent/10"
                                : "border-border text-muted2 hover:text-ink"
                            }`}
                          >
                            {s}
                            {isBooked && (
                              <span className="block text-[9px] uppercase tracking-widest no-underline mt-0.5 text-muted2/60">Booked</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted2 mt-3">
                      {date ? "Booked slots are greyed out. Times shown in IST." : "Pick a date to see available times. Times shown in IST."}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted2 block mb-4">What do you need? *</label>
                  <div className="flex flex-wrap gap-3" data-testid="needs-group">
                    {NEEDS.map((n) => (
                      <button
                        key={n} type="button" onClick={() => toggleNeed(n)}
                        data-testid={`need-${n.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        className={`rounded-full px-4 py-2 text-sm border transition-colors duration-300 ${
                          form.needs.includes(n) ? "bg-accent text-bg border-accent" : "border-border text-muted2 hover:text-ink hover:border-ink"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2 block mb-4">Budget range</label>
                    <div className="flex flex-col gap-2" data-testid="budget-group">
                      {BUDGETS.map((b) => (
                        <button
                          key={b} type="button" onClick={() => set("budget", b)}
                          data-testid={`budget-${b.replace(/[^a-zA-Z0-9]/g, "")}`}
                          className={`text-left rounded-lg px-4 py-3 border transition-colors duration-300 ${
                            form.budget === b ? "border-accent text-ink bg-accent/10" : "border-border text-muted2 hover:text-ink"
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted2 block mb-4">Timeline</label>
                    <div className="flex flex-col gap-2" data-testid="timeline-group">
                      {TIMELINES.map((t) => (
                        <button
                          key={t} type="button" onClick={() => set("timeline", t)}
                          data-testid={`timeline-${t.replace(/[^a-zA-Z0-9]/g, "")}`}
                          className={`text-left rounded-lg px-4 py-3 border transition-colors duration-300 ${
                            form.timeline === t ? "border-accent text-ink bg-accent/10" : "border-border text-muted2 hover:text-ink"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-muted2">Tell us the story</label>
                  <textarea data-testid="input-message" rows={4} className={inputCls + " resize-none"} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="What are you building, and where are you stuck?" />
                </div>

                <Magnetic strength={0.3}>
                  <button
                    type="submit" disabled={submitting}
                    data-testid="submit-intake"
                    className="inline-flex items-center gap-2 rounded-full bg-accent text-bg px-8 py-4 font-medium hover:bg-accent-hover transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Request my call →"}
                  </button>
                </Magnetic>
              </motion.form>
            ) : (
              <motion.div key="confirmed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="confirmation-section">
                <div className="rounded-xl border border-border bg-surface p-8">
                  <p className="font-serif text-3xl md:text-4xl">Thanks, {form.name.split(" ")[0]}. Your slot is in.</p>
                  <p className="text-muted2 mt-4 leading-relaxed">
                    You asked for <span className="text-ink">{date && format(date, "EEE, d MMM")}</span> at <span className="text-ink">{time}</span>.
                    We'll reach out over {form.email ? "email" : "WhatsApp"} to confirm the online meeting and share the link.
                  </p>
                  <p className="text-muted2 text-sm mt-6">
                    Prefer to reach us directly? Email <a href="mailto:Info@qissastudio.co" className="text-accent">Info@qissastudio.co</a>.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
